/**
 * Procedural quadruped rig (salamander / lizard / synapsid): smooth tube body, articulated
 * legs, waving tail, head with jaw + eyes. Fully parametric so stages can morph it live
 * (bulk, colours, limb length, sprawl) and animate it with a gait driven by travelled
 * distance so feet plant convincingly.
 */
import { Color, Group, Mesh, MeshStandardMaterial, Object3D, SphereGeometry, Vector3 } from 'three';
import { clamp, damp, lerp } from '@core/utils/math';
import { applyPattern, limbGeometry, sharedMat, tubeGeometry, type SkinPattern } from './geometry';

export interface LegSpec {
  /** Attachment along the body (0 = shoulder, 1 = hip). */
  t: number;
  upper: number;
  lower: number;
  radius: number;
  /** Lateral offset from the spine. */
  spread: number;
}

export interface QuadrupedSpec {
  /** Body length from neck to hip. */
  length: number;
  radius: number;
  /** Cross-section width/height ratio. */
  flatten?: number;
  head: { length: number; radius: number; flatten?: number };
  tail: { length: number; segments: number; radius: number };
  legs: LegSpec[];
  top: string;
  belly: string;
  eye?: { size: number; color?: string; spacing?: number };
  /** 0 upright (mammal) .. 1 sprawling (lizard). */
  sprawl: number;
  /** Ground clearance (hip height). */
  clearance: number;
  roughness?: number;
  /** Radial detail. */
  detail?: number;
  jaw?: boolean;
  /** Optional procedural skin pattern on torso/head/tail. */
  pattern?: SkinPattern;
  /** Optional dorsal crest/spikes: count + height. */
  crest?: { count: number; height: number; color?: string };
}

export interface RigState {
  /** 0..1.5 (fraction of run speed). */
  speedFrac: number;
  grounded: boolean;
  swimming: boolean;
  airborne: boolean;
  climbing?: boolean;
  gliding?: boolean;
  vy: number;
  /** Bite/lunge progress 0..1 (0 = none). */
  attack: number;
  /** Ability (tail whip) progress 0..1. */
  spin?: number;
  /** Hurt flash 0..1. */
  hurt: number;
  /** Lateral turn rate for banking (rad/s). */
  turnRate?: number;
}

interface Leg {
  hip: Group;
  knee: Group;
  side: number;
  phase: number;
  spec: LegSpec;
  upperMesh: Mesh;
  lowerMesh: Mesh;
  front: boolean;
}

export class Quadruped {
  readonly root = new Group();
  readonly body = new Group();
  readonly torso: Mesh;
  readonly head = new Group();
  readonly headMesh: Mesh;
  readonly jaw?: Group;
  readonly tail: Group[] = [];
  readonly legs: Leg[] = [];
  readonly material: MeshStandardMaterial;
  readonly eyes: Mesh[] = [];
  readonly crestMeshes: Mesh[] = [];
  readonly eyeMat: MeshStandardMaterial;
  phase = 0;
  private bob = 0;
  private pitch = 0;
  private roll = 0;
  private headYaw = 0;
  private baseEmissive = new Color(0);
  spec: QuadrupedSpec;
  /** Extra bulk applied to the torso (1 = normal). */
  bulk = 1;
  /** Extra limb length multiplier. */
  limbScale = 1;
  /** Extra multiplier for the front limbs only (arms → wings). */
  frontLimbScale = 1;
  /** Extra head scale (1 = normal). */
  headScale = 1;

  constructor(spec: QuadrupedSpec) {
    this.spec = spec;
    const detail = spec.detail ?? 12;
    const top = new Color(spec.top);
    const belly = new Color(spec.belly);
    this.material = new MeshStandardMaterial({ vertexColors: true, roughness: spec.roughness ?? 0.6, metalness: 0.02 });
    const ec = spec.eye?.color ?? '#1a1208';
    this.eyeMat = sharedMat(`eye${ec}`, () => new MeshStandardMaterial({ color: new Color(ec), roughness: 0.2, metalness: 0.1 }));
    const L = spec.length;
    const R = spec.radius;
    // Torso: neck (+Z) to hip (-Z), origin at the middle of the body.
    const torsoGeo = tubeGeometry({
      points: [new Vector3(0, 0, L * 0.5), new Vector3(0, R * 0.05, L * 0.15), new Vector3(0, R * 0.08, -L * 0.2), new Vector3(0, 0, -L * 0.5)],
      radius: [R * 0.7, R * 1.0, R * 0.95, R * 0.6],
      segments: 14,
      radial: detail,
      flatten: spec.flatten ?? 1.15,
      top,
      belly,
    });
    if (spec.pattern) applyPattern(torsoGeo, spec.pattern);
    this.torso = new Mesh(torsoGeo, this.material);
    this.torso.castShadow = true;
    this.body.add(this.torso);
    this.body.position.y = spec.clearance;
    this.root.add(this.body);

    // Head
    const H = spec.head;
    const headGeo = tubeGeometry({
      points: [new Vector3(0, 0, 0), new Vector3(0, H.radius * 0.1, H.length * 0.45), new Vector3(0, -H.radius * 0.05, H.length)],
      radius: [H.radius * 0.85, H.radius, H.radius * 0.45],
      segments: 10,
      radial: detail,
      flatten: H.flatten ?? 1.25,
      top,
      belly,
    });
    if (spec.pattern) applyPattern(headGeo, { ...spec.pattern, amount: (spec.pattern.amount ?? 0.8) * 0.6 }, L * 0.5);
    this.headMesh = new Mesh(headGeo, this.material);
    this.headMesh.castShadow = true;
    this.head.position.set(0, R * 0.25, L * 0.5 + R * 0.3);
    this.head.add(this.headMesh);
    this.body.add(this.head);
    if (spec.jaw !== false) {
      const jaw = new Group();
      const jawGeo = tubeGeometry({
        points: [new Vector3(0, 0, 0), new Vector3(0, -H.radius * 0.05, H.length * 0.5), new Vector3(0, 0, H.length * 0.9)],
        radius: [H.radius * 0.55, H.radius * 0.6, H.radius * 0.3],
        segments: 6,
        radial: Math.max(6, detail - 4),
        flatten: (H.flatten ?? 1.25) * 1.05,
        top: belly.clone().lerp(top, 0.3),
        belly,
      });
      const jawMesh = new Mesh(jawGeo, this.material);
      jaw.position.set(0, -H.radius * 0.45, 0);
      jaw.add(jawMesh);
      this.head.add(jaw);
      (this as { jaw?: Group }).jaw = jaw;
    }
    // Eyes
    const eye = spec.eye ?? { size: R * 0.25 };
    const eyeGeo = new SphereGeometry(eye.size, 12, 10);
    const whiteMat = sharedMat('eyewhite', () => new MeshStandardMaterial({ color: '#f4efd8', roughness: 0.35 }));
    for (const side of [-1, 1]) {
      const e = new Mesh(eyeGeo, whiteMat);
      e.position.set(side * (eye.spacing ?? H.radius * 0.8), H.radius * 0.55, H.length * 0.45);
      const pupil = new Mesh(new SphereGeometry(eye.size * 0.55, 8, 8), this.eyeMat);
      pupil.position.set(side * eye.size * 0.45, eye.size * 0.1, eye.size * 0.55);
      e.add(pupil);
      this.head.add(e);
      this.eyes.push(e);
    }
    // Tail chain
    const T = spec.tail;
    let parent: Object3D = this.body;
    let prevZ = -L * 0.5 + R * 0.2;
    for (let i = 0; i < T.segments; i++) {
      const g = new Group();
      const segLen = T.length / T.segments;
      const r0 = T.radius * (1 - i / T.segments) * 0.95 + 0.02;
      const r1 = T.radius * (1 - (i + 1) / T.segments) * 0.95 + 0.015;
      const geo = tubeGeometry({
        points: [new Vector3(0, 0, 0.05), new Vector3(0, 0, -segLen * 0.5), new Vector3(0, 0, -segLen - 0.02)],
        radius: [r0, (r0 + r1) * 0.5, r1],
        segments: 4,
        radial: Math.max(6, detail - 4),
        top,
        belly,
      });
      if (spec.pattern) applyPattern(geo, spec.pattern, -L * 0.5 - i * segLen);
      const mesh = new Mesh(geo, this.material);
      mesh.castShadow = i < 1;
      g.add(mesh);
      g.position.set(0, i === 0 ? -R * 0.05 : 0, i === 0 ? prevZ : -segLen);
      parent.add(g);
      this.tail.push(g);
      parent = g;
    }
    // Legs
    for (const ls of spec.legs) {
      for (const side of [-1, 1]) {
        const hip = new Group();
        const z = L * 0.5 - ls.t * L;
        hip.position.set(side * ls.spread, -R * 0.35, z);
        const upperGeo = limbGeometry(ls.upper, [ls.radius, ls.radius * 0.85, ls.radius * 0.7], 8, 4, top, belly);
        const upperMesh = new Mesh(upperGeo, this.material);
        upperMesh.castShadow = true;
        hip.add(upperMesh);
        const knee = new Group();
        knee.position.set(0, 0, ls.upper);
        const lowerGeo = limbGeometry(ls.lower, [ls.radius * 0.7, ls.radius * 0.55, ls.radius * 0.6], 8, 4, top, belly);
        const lowerMesh = new Mesh(lowerGeo, this.material);
        lowerMesh.castShadow = false;
        knee.add(lowerMesh);
        // foot (toes): a flattened little tube
        const footGeo = tubeGeometry({
          points: [new Vector3(0, 0, 0), new Vector3(0, 0, ls.radius * 1.6)],
          radius: [ls.radius * 0.65, ls.radius * 0.5],
          segments: 3,
          radial: 6,
          flatten: 1.8,
          top,
          belly,
        });
        const foot = new Mesh(footGeo, this.material);
        foot.position.set(0, 0, ls.lower);
        foot.rotation.x = -Math.PI * 0.45;
        knee.add(foot);
        hip.add(knee);
        this.body.add(hip);
        const front = ls.t < 0.5;
        this.legs.push({ hip, knee, side, phase: 0, spec: ls, upperMesh, lowerMesh, front });
      }
    }
    // Diagonal trot phases: FL & BR together, FR & BL together.
    for (const leg of this.legs) {
      const idx = spec.legs.indexOf(leg.spec);
      leg.phase = ((idx % 2 === 0 ? 0 : Math.PI) + (leg.side > 0 ? Math.PI : 0)) % (Math.PI * 2);
    }
    // Crest
    if (spec.crest) {
      const cc = spec.crest.color ?? spec.top;
      const cm = sharedMat(`crest${cc}`, () => new MeshStandardMaterial({ color: new Color(cc), roughness: 0.6 }));
      for (let i = 0; i < spec.crest.count; i++) {
        const t = i / Math.max(1, spec.crest.count - 1);
        const geo = tubeGeometry({
          points: [new Vector3(0, 0, 0), new Vector3(0, spec.crest.height, 0.02)],
          radius: [R * 0.18, 0.01],
          segments: 3,
          radial: 5,
          flatten: 0.5,
        });
        const s = new Mesh(geo, cm);
        s.position.set(0, R * 0.95, L * 0.4 - t * L * 0.85);
        s.visible = false;
        this.body.add(s);
        this.crestMeshes.push(s);
      }
    }
    this.pose({ speedFrac: 0, grounded: true, swimming: false, airborne: false, vy: 0, attack: 0, hurt: 0 }, 0);
  }

  /** Set hurt flash colour baseline (call once). */
  setEmissive(c: Color): void {
    this.baseEmissive.copy(c);
    this.material.emissive.copy(c);
  }

  /** Advance the gait by a travelled distance (metres). */
  advance(distance: number, strideLength: number): void {
    this.phase += (distance / Math.max(0.05, strideLength)) * Math.PI * 2;
  }

  /** Apply the animated pose. dt is the render-frame delta (visual smoothing only). */
  pose(s: RigState, dt: number): void {
    const spec = this.spec;
    const sp = clamp(s.speedFrac, 0, 1.5);
    const t = this.phase;
    const sprawl = spec.sprawl;
    const swimming = s.swimming;
    const air = s.airborne && !swimming;
    // Body bob + pitch
    const bobTarget = swimming ? Math.sin(t * 1.0) * 0.03 : Math.abs(Math.sin(t)) * 0.05 * sp * spec.clearance * 2;
    this.bob = damp(this.bob, bobTarget, 14, dt);
    let pitchT = 0;
    if (air) pitchT = clamp(-s.vy * 0.06, -0.5, 0.35);
    else if (swimming) pitchT = 0.05;
    else if (s.climbing) pitchT = -0.9;
    if (s.gliding) pitchT = 0.12;
    this.pitch = damp(this.pitch, pitchT, 8, dt);
    this.roll = damp(this.roll, clamp((s.turnRate ?? 0) * -0.08, -0.35, 0.35) * (s.gliding ? 2 : 0.6), 6, dt);
    this.body.position.y = spec.clearance * (swimming ? 0.6 : s.climbing ? 0.55 : 1) + this.bob;
    this.body.rotation.x = this.pitch;
    this.body.rotation.z = this.roll;
    // Spine undulation (lateral) — lizards swing the torso, swimmers wave it.
    const und = swimming ? 0.16 : 0.09 * sprawl * sp;
    this.torso.rotation.y = Math.sin(t) * und;
    this.torso.scale.set(this.bulk, this.bulk, 1);
    // Head: look along the turn + attack lunge
    const lunge = s.attack > 0 ? Math.sin(s.attack * Math.PI) : 0;
    this.head.position.z = spec.length * 0.5 + spec.radius * 0.3 + lunge * spec.head.length * 0.6;
    this.headYaw = damp(this.headYaw, Math.sin(t) * und * 0.8 - clamp((s.turnRate ?? 0) * 0.05, -0.4, 0.4), 10, dt);
    this.head.rotation.y = this.headYaw;
    this.head.scale.setScalar(this.headScale);
    this.head.rotation.x = (swimming ? 0.15 : 0) - lunge * 0.25;
    if (this.jaw) this.jaw.rotation.x = lunge * 0.7 + (s.hurt > 0 ? 0.3 : 0);
    // Tail wave
    const tailAmp = swimming ? 0.55 : air ? 0.15 : 0.12 + 0.3 * sp;
    const tailSpeed = swimming ? 1.6 : 1;
    const spin = s.spin ?? 0;
    for (let i = 0; i < this.tail.length; i++) {
      const g = this.tail[i];
      const k = (i + 1) / this.tail.length;
      let yaw = Math.sin(t * tailSpeed - i * 0.7) * tailAmp * k;
      if (spin > 0) yaw += Math.sin(spin * Math.PI * 2) * 0.9 * k;
      g.rotation.y = yaw;
      g.rotation.x = swimming ? Math.sin(t * 0.7 - i * 0.5) * 0.06 : air ? -0.25 * k : Math.sin(t * 0.5 - i) * 0.03;
    }
    // Legs
    for (const leg of this.legs) {
      const ph = t + leg.phase;
      const swing = Math.sin(ph);
      const lift = Math.max(0, Math.sin(ph + Math.PI * 0.5));
      const ls = leg.spec;
      const hip = leg.hip;
      const knee = leg.knee;
      hip.scale.setScalar(this.limbScale * (leg.front ? this.frontLimbScale : 1));
      if (swimming) {
        // Tuck legs back along the body
        hip.rotation.set(damp(hip.rotation.x, Math.PI * 0.85, 8, dt), damp(hip.rotation.y, leg.side * 0.3, 8, dt), 0);
        knee.rotation.x = damp(knee.rotation.x, 0.4, 8, dt);
        continue;
      }
      if (air || s.gliding) {
        const tx = leg.front ? Math.PI * 0.75 : Math.PI * 0.5;
        hip.rotation.x = damp(hip.rotation.x, s.gliding ? Math.PI * 0.5 : tx, 8, dt);
        hip.rotation.y = damp(hip.rotation.y, leg.side * (s.gliding ? 1.3 : 0.5), 8, dt);
        hip.rotation.z = damp(hip.rotation.z, 0, 8, dt);
        knee.rotation.x = damp(knee.rotation.x, s.gliding ? -0.2 : -0.9, 8, dt);
        continue;
      }
      // Base stance: leg points down (rotate the +Z limb to -Y), splayed by sprawl.
      const stanceX = Math.PI * 0.5 + (leg.front ? -0.15 : 0.15) * (1 - sprawl);
      const stanceY = leg.side * (0.35 + sprawl * 0.9);
      const amp = 0.55 + sprawl * 0.35;
      const idleShift = Math.sin(ph * 0.15) * 0.02;
      // Sprawling legs swing mostly in yaw, upright legs mostly in pitch.
      const targetX = stanceX + swing * amp * sp * (1 - sprawl * 0.6) + idleShift - lift * 0.5 * sp;
      const targetY = stanceY - leg.side * swing * amp * sp * sprawl * 0.9;
      const targetZ = -leg.side * (0.15 + sprawl * 0.55) - leg.side * lift * 0.35 * sp * sprawl;
      hip.rotation.x = damp(hip.rotation.x, targetX, 30, dt);
      hip.rotation.y = damp(hip.rotation.y, targetY, 30, dt);
      hip.rotation.z = damp(hip.rotation.z, targetZ, 30, dt);
      const kneeT = -(0.6 + sprawl * 0.5) - lift * 0.9 * sp + (s.climbing ? -0.3 : 0);
      knee.rotation.x = damp(knee.rotation.x, kneeT, 30, dt);
      knee.rotation.y = 0;
      void ls;
    }
    // Hurt flash
    if (s.hurt > 0) this.material.emissive.setRGB(0.9 * s.hurt, 0.15 * s.hurt, 0.1 * s.hurt).add(this.baseEmissive);
    else this.material.emissive.copy(this.baseEmissive);
  }

  /** Recolour (top/belly gradient baked into vertex colours → tint via material colour). */
  tint(c: Color): void {
    this.material.color.copy(c);
  }

  showCrest(frac: number): void {
    const n = Math.round(frac * this.crestMeshes.length);
    this.crestMeshes.forEach((m, i) => {
      m.visible = i < n;
      const k = clamp(frac * this.crestMeshes.length - i, 0, 1);
      m.scale.setScalar(Math.max(0.01, lerp(0.2, 1, k)));
    });
  }
}
